import {
  initializeTableIds as initializeTableIdsUtil,
  extractOriginalIndexFromId,
} from "../utils/pdfTableViewerUtils";
import { makeNewId } from "common/ids";
import { useCallback } from "react";

/**
 * Hook for managing table ID system - column and row unique identifiers
 * Coordinates ID generation, verification, and management with state
 */
const useTableIdSystem = (idSystemState, tableData) => {
  const {
    columnIds,
    setColumnIds,
    rowIds,
    setRowIds,
    originalTableStructure,
    setOriginalTableStructure,
    deletedColumns,
    setDeletedColumns,
    deletedRows,
    setDeletedRows,
  } = idSystemState;

  // Utility for generating unique IDs
  const uid = useCallback(() => makeNewId(), []);

  // Generate unique ID for column with readable context
  const generateColumnId = useCallback(
    (columnIndex, header) => {
      // Create a unique ID with some readable context
      return `col_${columnIndex}_${(header || "untitled")
        .replace(/[^a-zA-Z0-9]/g, "_")
        .toLowerCase()}_${uid()}`;
    },
    [uid]
  );

  // Generate unique ID for row with readable context
  const generateRowId = useCallback(
    (rowIndex, rowContent) => {
      // Create a unique ID with some readable context
      const preview = (rowContent?.[0] || "empty")
        .replace(/[^a-zA-Z0-9]/g, "_")
        .toLowerCase()
        .substring(0, 20);
      return `row_${rowIndex}_${preview}_${uid()}`;
    },
    [uid]
  );

  // Initialize table IDs with state management
  const initializeTableIds = useCallback(
    (newTableData) => {
      if (!newTableData) {
        return null;
      }

      // Use extracted utility function for ID generation
      const {
        columnIds: newColumnIds,
        rowIds: newRowIds,
      } = initializeTableIdsUtil(newTableData);

      // IMMEDIATELY SET ALL ID STATE
      setColumnIds(newColumnIds);
      setRowIds(newRowIds);

      // Store original structure for recovery - COMPLETE BASELINE
      const originalStructure = {
        headers: [...newTableData.headers],
        rows: newTableData.rows.map((row) => [...row]),
        columnIds: [...newColumnIds],
        rowIds: [...newRowIds],
      };
      setOriginalTableStructure(originalStructure);

      // Clear any existing deletions when new table is loaded
      setDeletedColumns(new Map());
      setDeletedRows(new Map());

      return { columnIds: newColumnIds, rowIds: newRowIds };
    },
    [
      setColumnIds,
      setRowIds,
      setOriginalTableStructure,
      setDeletedColumns,
      setDeletedRows,
    ]
  );

  // Regenerate IDs for existing table structure (e.g., after grid line changes)
  const regenerateTableIds = useCallback(
    (newTableData) => {
      // Clear existing deletions since table structure has changed
      setDeletedColumns(new Map());
      setDeletedRows(new Map());

      // Generate new IDs for the new structure
      const newStructure = initializeTableIds(newTableData);

      return newStructure;
    },
    [initializeTableIds, setDeletedColumns, setDeletedRows]
  );

  // Clear deletion tracking (useful when loading new tables)
  const clearDeletionTracking = useCallback(() => {
    setDeletedColumns(new Map());
    setDeletedRows(new Map());
  }, [setDeletedColumns, setDeletedRows]);

  // Get statistics about current ID system
  const getIdSystemStats = useCallback(() => {
    return {
      columnCount: columnIds?.length || 0,
      rowCount: rowIds?.length || 0,
      deletedColumnCount: deletedColumns?.size || 0,
      deletedRowCount: deletedRows?.size || 0,
      hasOriginalStructure: !!originalTableStructure,
    };
  }, [columnIds, rowIds, deletedColumns, deletedRows, originalTableStructure]);

  return {
    // Core operations
    initializeTableIds,
    regenerateTableIds,
    clearDeletionTracking,

    // Utilities
    generateColumnId,
    generateRowId,
    extractOriginalIndexFromId,

    // Information
    getIdSystemStats,

    // Direct state access for advanced use cases
    columnIds,
    rowIds,
    deletedColumns,
    deletedRows,
    originalTableStructure,
  };
};

export default useTableIdSystem;
